'use client';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useMergeRefs } from '@floating-ui/react';
import { ChevronUpIcon, ChevronDownIcon } from '@navikt/aksel-icons';
import cl from 'clsx/lite';
import { useContext, useRef } from 'react';
import { Paragraph } from '../../paragraph/paragraph.js';
import { ComboboxContext } from '../ComboboxContext.js';
import { useComboboxIdDispatch } from '../ComboboxIdContext.js';
import { omit } from '../omit/omit.js';
import { prefix } from '../utilities.js';
import ComboboxChips from './ComboboxChips.js';
import ComboboxClearButton from './ComboboxClearButton.js';

const ComboboxInput = ({ hideClearButton, listId, error, hideChips, handleKeyDown, ...rest }) => {
    const context = useContext(ComboboxContext);
    const idDispatch = useComboboxIdDispatch();
    const clearButtonRef = useRef(null);
    if (!context) {
        throw new Error('ComboboxContext is missing');
    }
    const setActiveIndex = (id) => {
        idDispatch?.({ type: 'SET_ACTIVE_INDEX', payload: id });
    };
    const { forwareddRef, readOnly, disabled, open, inputRef, refs, inputValue, multiple, selectedOptions, formFieldProps, htmlSize, options, setOpen, getReferenceProps, setInputValue, handleSelectOption, size, } = context;
    const mergedRefs = useMergeRefs([forwareddRef, inputRef]);
    // onChange function for the input
    const onChange = (event) => {
        const value = event.target.value;
        setInputValue(value);
        setActiveIndex(0);
        // check if input value is the same as a label, if so, select it
        for (const option of Object.values(options)) {
            if (option.label.toLowerCase() === value.toLowerCase()) {
                /* if option is already selected, discard selecting it, since it would de-select */
                if (selectedOptions[prefix(option.value)])
                    continue;
                handleSelectOption({ option });
            }
        }
    };
    const showClearButton = !hideClearButton && Object.keys(selectedOptions).length > 0;
    /* Props from floating-ui */
    const props = getReferenceProps({
        ref: refs?.setReference,
        role: null,
        'aria-controls': null,
        'aria-expanded': null,
        'aria-haspopup': null,
        /* If we click the wrapper, toggle open, set index to first option, and focus the input */
        onClick(event) {
            if (disabled)
                return;
            if (readOnly)
                return;
            if (clearButtonRef.current?.contains(event.target))
                return;
            setOpen(!open);
            setActiveIndex(0);
            inputRef.current?.focus();
        },
        /* Handles list navigation */
        onKeyDown: handleKeyDown,
        // preventDefault on keydown to avoid sending in form
        onKeyPress(event) {
            if (event.key === 'Enter') {
                event.preventDefault();
            }
        },
    });
    return (jsx(Paragraph, { "data-size": size, asChild: true, children: jsxs("div", { ...props, "aria-disabled": disabled ? 'true' : undefined, className: cl('ds-textfield__input', 'ds-combobox__input__wrapper', readOnly && 'ds-combobox--readonly', error && 'ds-combobox--error'), children: [jsxs("div", { className: 'ds-combobox__chip-and-input', children: [multiple && !hideChips && jsx(ComboboxChips, {}), jsx(Paragraph, { "data-size": size, asChild: true, children: jsx("input", { ref: mergedRefs, "aria-activedescendant": props['aria-activedescendant'], readOnly: readOnly, "aria-autocomplete": 'list', role: 'combobox', "aria-expanded": open, "aria-controls": open ? listId : undefined, autoComplete: 'off', size: htmlSize, value: inputValue, ...omit(['style', 'className'], rest), ...formFieldProps.inputProps, className: 'ds-combobox__input', onChange: (e) => {
                                    onChange(e);
                                    !open && setOpen(true);
                                    rest.onChange?.(e);
                                } }) })] }), showClearButton && jsx(ComboboxClearButton, { ref: clearButtonRef }), jsx("div", { className: 'ds-combobox__arrow', children: open ? (jsx(ChevronUpIcon, { title: 'arrow up', height: '1.5em', width: '1.5em' })) : (jsx(ChevronDownIcon, { title: 'arrow down', height: '1.5em', width: '1.5em' })) })] }) }));
};
ComboboxInput.displayName = 'ComboboxInput';

export { ComboboxInput as default };
